import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useId,
  useCallback,
  memo,
} from "react";
import { withMask } from "use-mask-input";
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  PaginationState,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedUniqueValues,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleX,
  Columns3,
  Ellipsis,
  Plus,
  Trash2,
  Phone,
  Mail,
  Calendar,
  Search,
  Filter,
  ListFilter,
  CheckCircle,
  AlertCircle,
  X,
  Loader2Icon,
} from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { api } from "../../services/api";
import FilterDrawer, { CollegeFilters } from "./FilterDrawer";
import { Loader } from "../ui/loader";

interface College {
  id: string;
  name: string;
  code: string;
  address: string;
  phone: string;
  email: string;
  establishedYear: number;
  isActive: boolean;
}

// College Form Validation Schema
const CollegeFormSchema = z.object({
  name: z
    .string()
    .min(1, { message: "College name is required" })
    .min(3, { message: "College name must be at least 3 characters" })
    .max(100, { message: "College name must not exceed 100 characters" }),
  code: z
    .string()
    .min(1, { message: "College code is required" })
    .min(2, { message: "College code must be at least 2 characters" })
    .max(10, { message: "College code must not exceed 10 characters" })
    .regex(/^[A-Z0-9]+$/, {
      message: "College code must contain only uppercase letters and numbers",
    }),
  email: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Please enter a valid email address" }),
  phone: z
    .string()
    .min(1, { message: "Phone number is required" })
    .regex(/^[\+]?[0-9\s\-\(\)]{10,15}$/, {
      message: "Please enter a valid phone number",
    }),
  address: z
    .string()
    .min(1, { message: "Address is required" })
    .min(10, { message: "Address must be at least 10 characters" })
    .max(500, { message: "Address must not exceed 500 characters" }),
  establishedYear: z
    .number()
    .min(1800, { message: "Established year must be 1800 or later" })
    .max(new Date().getFullYear(), {
      message: "Established year cannot be in the future",
    }),
  isActive: z.boolean(),
});

type CollegeFormData = z.infer<typeof CollegeFormSchema>;

// Custom filter function for multi-column searching
const multiColumnFilterFn: FilterFn<College> = (row, columnId, filterValue) => {
  const searchableRowContent =
    `${row.original.name} ${row.original.code} ${row.original.address}`.toLowerCase();
  const searchTerm = (filterValue ?? "").toLowerCase();
  return searchableRowContent.includes(searchTerm);
};

const statusFilterFn: FilterFn<College> = (
  row,
  columnId,
  filterValue: string[]
) => {
  if (!filterValue?.length) return true;
  const status = row.getValue(columnId) as boolean;
  const statusString = status ? "Active" : "Inactive";
  return filterValue.includes(statusString);
};

// Memoized search input component to prevent focus loss
interface SearchInputProps {
  id: string;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  searchInProgress: boolean;
}

const SearchInput = memo<SearchInputProps>(
  ({ id, searchTerm, onSearchChange, onSearchClear, searchInProgress }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    return (
      <div className="relative bg-white flex-1">
        <Input
          id={`${id}-input`}
          ref={inputRef}
          className={cn("ps-9", Boolean(searchTerm) && "pe-9")}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Filter by name, code, or address..."
          type="text"
          aria-label="Filter by name, code, or address"
        />
        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
          {searchInProgress ? (
            <Loader2Icon className="animate-spin h-4 w-4" />
          ) : (
            <Search className="stroke-2 h-4 w-4" aria-hidden="true" />
          )}
        </div>
        {Boolean(searchTerm) && (
          <button
            className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-lg text-muted-foreground/80 outline-offset-2 transition-colors hover:text-foreground focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Clear filter"
            onClick={onSearchClear}
          >
            <CircleX className="stroke-2 h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }
);

const Colleges = () => {
  const id = useId();

  // Local state management (no longer using Redux for filtered results)
  const [colleges, setColleges] = useState<College[]>([]);
  const [localPagination, setLocalPagination] = useState({
    total: 0,
    totalPages: 0,
    page: 1,
    limit: 10,
  });
  const [isLoading, setIsLoading] = useState(false);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "name",
      desc: false,
    },
  ]);

  // Add search state for server-side search
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Add a separate state to track if we're in the middle of a search operation
  // This prevents unnecessary re-renders that cause input focus loss
  const [searchInProgress, setSearchInProgress] = useState(false);

  // Filter drawer state
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<CollegeFilters>({
    status: [],
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCollege, setEditingCollege] = useState<College | null>(null);

  // Alert state for showing success/error messages
  const [alert, setAlert] = useState<{
    show: boolean;
    type: "success" | "error";
    title: string;
    message: string;
  }>({
    show: false,
    type: "success",
    title: "",
    message: "",
  });

  // Helper function to show alert messages
  const showAlert = (
    type: "success" | "error",
    title: string,
    message: string
  ) => {
    setAlert({
      show: true,
      type,
      title,
      message,
    });

    // Auto-hide success alerts after 5 seconds
    if (type === "success") {
      setTimeout(() => {
        setAlert((prev) => ({ ...prev, show: false }));
      }, 5000);
    }
  };

  // Helper function to hide alert
  const hideAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  // Form validation using react-hook-form with Zod
  const createForm = useForm<CollegeFormData>({
    resolver: zodResolver(CollegeFormSchema),
    defaultValues: {
      name: "",
      code: "",
      address: "",
      phone: "",
      email: "",
      establishedYear: new Date().getFullYear(),
      isActive: true,
    },
  });

  const editForm = useForm<CollegeFormData>({
    resolver: zodResolver(CollegeFormSchema),
    defaultValues: {
      name: "",
      code: "",
      address: "",
      phone: "",
      email: "",
      establishedYear: new Date().getFullYear(),
      isActive: true,
    },
  });

  const handleEditCollege = (college: College) => {
    setEditingCollege(college);
    editForm.reset({
      name: college.name,
      code: college.code,
      address: college.address,
      phone: college.phone,
      email: college.email,
      establishedYear: college.establishedYear,
      isActive: college.isActive,
    });
    setIsEditDialogOpen(true);
  };

  const handleDeleteRows = () => {
    const selectedRows = table.getSelectedRowModel().rows;
    const selectedColleges = selectedRows.map((row) => row.original);
    const collegeIds = selectedColleges.map((college) => college.id);

    // Use the new bulk delete endpoint - automatically handles single or bulk operations
    const idsParam = collegeIds.join(",");

    api
      .delete(`/colleges/${idsParam}`)
      .then(() => {
        // Show success alert
        const collegeCount = selectedColleges.length;
        const collegeNames = selectedColleges
          .slice(0, 3)
          .map((c) => c.name)
          .join(", ");
        const displayNames =
          collegeCount > 3
            ? `${collegeNames} and ${collegeCount - 3} more`
            : collegeNames;

        showAlert(
          "success",
          "Colleges Deleted Successfully",
          `${
            collegeCount === 1 ? "College" : `${collegeCount} colleges`
          } ${displayNames} ${
            collegeCount === 1 ? "has" : "have"
          } been deleted successfully.`
        );

        // Refresh data using the filter endpoint instead of Redux
        const filterParams = {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          search: debouncedSearchTerm || undefined,
          status: activeFilters.status.join(","),
        };

        setIsLoading(true);
        return api.get("/colleges/filter", { params: filterParams });
      })
      .then((response) => {
        if (response) {
          const { data, meta: responsePagination } = response.data;
          setColleges(data || []);
          setLocalPagination({
            total: responsePagination?.total || 0,
            totalPages: responsePagination?.totalPages || 0,
            page: responsePagination?.page || 1,
            limit: responsePagination?.limit || 10,
          });
        }
        table.resetRowSelection();
      })
      .catch((error) => {
        console.error("Failed to delete colleges:", error);
        showAlert(
          "error",
          "Delete Failed",
          error.response?.data?.message ||
            "Failed to delete colleges. Please try again."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Function to refresh data using filter endpoint
  const refreshData = async () => {
    const filterParams = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedSearchTerm || undefined,
      status: activeFilters.status.join(","),
    };

    setIsLoading(true);

    try {
      const response = await api.get("/colleges/filter", {
        params: filterParams,
      });
      const { data, meta: responsePagination } = response.data;
      setColleges(data || []);
      setLocalPagination({
        total: responsePagination?.total || 0,
        totalPages: responsePagination?.totalPages || 0,
        page: responsePagination?.page || 1,
        limit: responsePagination?.limit || 10,
      });
    } catch (error) {
      console.error("Failed to refresh colleges:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyFilters = (filters: CollegeFilters) => {
    setActiveFilters(filters);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleOpenFilterDrawer = () => {
    setIsFilterDrawerOpen(true);
  };

  const handleCloseFilterDrawer = () => {
    setIsFilterDrawerOpen(false);
  };

  // Function to get active filter count for display
  const getActiveFilterCount = () => {
    return activeFilters.status.length;
  };

  const columns = useMemo<ColumnDef<College>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        size: 28,
        enableSorting: false,
        enableHiding: false,
      },
      {
        header: "College Name",
        accessorKey: "name",
        cell: ({ row }) => (
          <>
            <div className="font-medium text-gray-900">{row.original.name}</div>
            <div className="text-sm text-gray-500">{row.original.code}</div>
          </>
        ),
        size: 280,
        filterFn: multiColumnFilterFn,
        enableHiding: false,
      },
      {
        header: "Address",
        accessorKey: "address",
        cell: ({ row }) => (
          <div className="text-sm text-gray-800 max-w-xs truncate">
            {row.getValue("address")}
          </div>
        ),
        size: 200,
      },
      {
        header: "Contact",
        accessorKey: "phone",
        cell: ({ row }) => {
          const college = row.original;
          return (
            <div className="space-y-1 flex flex-col">
              <span
                className="text-sm text-gray-800"
                style={{ wordBreak: "break-word" }}
              >
                {college.email}
              </span>
              <span
                className="text-sm text-gray-800"
                style={{ wordBreak: "break-word" }}
              >
                {college.phone}
              </span>
            </div>
          );
        },
        size: 200,
      },
      {
        header: "Established",
        accessorKey: "establishedYear",
        cell: ({ row }) => (
          <div className="flex items-center text-sm text-gray-800">
            <Calendar className="h-3 w-3 mr-1" />
            {row.getValue("establishedYear")}
          </div>
        ),
        size: 120,
      },
      {
        header: "Status",
        accessorKey: "isActive",
        cell: ({ row }) => {
          const isActive = row.getValue("isActive") as boolean;
          return (
            <Badge
              className={cn(
                !isActive && "bg-muted-foreground/60 text-primary-foreground"
              )}
            >
              {isActive ? "Active" : "Inactive"}
            </Badge>
          );
        },
        size: 100,
        filterFn: statusFilterFn,
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <RowActions
            row={row}
            onEdit={handleEditCollege}
            onRefresh={refreshData}
            onAlert={showAlert}
          />
        ),
        size: 60,
        enableHiding: false,
      },
    ],
    [refreshData, showAlert]
  );

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== debouncedSearchTerm) {
        setDebouncedSearchTerm(searchTerm);
        setSearchInProgress(false);
      }
    }, 500); // 500ms delay

    if (searchTerm !== debouncedSearchTerm) {
      setSearchInProgress(true);
    }

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  useEffect(() => {
    const filterParams = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedSearchTerm || undefined,
      status: activeFilters.status.join(","),
    };

    setIsLoading(true);

    api
      .get("/colleges/filter", { params: filterParams })
      .then((response) => {
        const { data, meta: responsePagination } = response.data;
        setColleges(data || []);
        setLocalPagination({
          total: responsePagination?.total || 0,
          totalPages: responsePagination?.totalPages || 0,
          page: responsePagination?.page || 1,
          limit: responsePagination?.limit || 10,
        });
      })
      .catch((error) => {
        console.error("Failed to fetch filtered colleges:", error);
        setColleges([]);
        setLocalPagination({
          total: 0,
          totalPages: 0,
          page: 1,
          limit: pagination.pageSize,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    debouncedSearchTerm,
    activeFilters,
  ]);

  // Use callback approach for search input to prevent re-renders
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchTerm("");
  }, []);

  const table = useReactTable({
    data: colleges || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    // Configure for server-side pagination
    manualPagination: true,
    pageCount: localPagination?.totalPages || 0,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    // Remove client-side filtering since we use server-side search
    // getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility,
    },
  });

  // Get unique status values
  const uniqueStatusValues = useMemo(() => {
    const statusColumn = table.getColumn("isActive");
    if (!statusColumn) return [];
    return ["Active", "Inactive"];
  }, [table.getColumn("isActive")?.getFacetedUniqueValues()]);

  // Get counts for each status
  const statusCounts = useMemo(() => {
    const activeCount = colleges?.filter((c) => c.isActive).length || 0;
    const inactiveCount = colleges?.filter((c) => !c.isActive).length || 0;
    return new Map([
      ["Active", activeCount],
      ["Inactive", inactiveCount],
    ]);
  }, [colleges]);

  const selectedStatuses = useMemo(() => {
    const filterValue = table
      .getColumn("isActive")
      ?.getFilterValue() as string[];
    return filterValue ?? [];
  }, [table.getColumn("isActive")?.getFilterValue()]);

  const handleStatusChange = (checked: boolean, value: string) => {
    const filterValue = table
      .getColumn("isActive")
      ?.getFilterValue() as string[];
    const newFilterValue = filterValue ? [...filterValue] : [];

    if (checked) {
      newFilterValue.push(value);
    } else {
      const index = newFilterValue.indexOf(value);
      if (index > -1) {
        newFilterValue.splice(index, 1);
      }
    }

    table
      .getColumn("isActive")
      ?.setFilterValue(newFilterValue.length ? newFilterValue : undefined);
  };

  const handleCreateCollege = async (data: CollegeFormData) => {
    setIsSubmitting(true);

    try {
      await api.post("/colleges", data);

      createForm.reset();
      setIsCreateDialogOpen(false);

      // Show success alert
      showAlert(
        "success",
        "College Created Successfully",
        `${data.name} (${data.code}) has been created successfully.`
      );

      // Refresh data using local state instead of Redux
      await refreshData();
    } catch (error: any) {
      console.error("Failed to create college:", error);

      // Show error alert
      showAlert(
        "error",
        "Failed to Create College",
        error.response?.data?.message ||
          "Failed to create college. Please check the information and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateCollege = async (data: CollegeFormData) => {
    if (!editingCollege) return;

    setIsSubmitting(true);

    try {
      await api.put(`/colleges/${editingCollege.id}`, data);

      editForm.reset();
      setIsEditDialogOpen(false);
      setEditingCollege(null);

      // Show success alert
      showAlert(
        "success",
        "College Updated Successfully",
        `${data.name} (${data.code}) has been updated successfully.`
      );

      // Refresh data using local state instead of Redux
      await refreshData();
    } catch (error: any) {
      console.error("Failed to update college:", error);

      // Show error alert
      showAlert(
        "error",
        "Failed to Update College",
        error.response?.data?.message ||
          "Failed to update college. Please check the information and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={handleCloseFilterDrawer}
        onApplyFilters={handleApplyFilters}
        currentFilters={activeFilters}
      />

      {/* Alert Messages */}
      {alert.show && (
        <div className="fixed top-4 right-4 z-50 w-96">
          <Alert
            variant={alert.type === "error" ? "destructive" : "default"}
            className={cn(
              "border shadow-lg",
              alert.type === "success" &&
                "border-green-200 bg-green-50 text-green-800",
              alert.type === "error" && "border-red-200 bg-red-50"
            )}
          >
            <div className="flex items-start gap-3">
              {alert.type === "success" ? (
                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <AlertTitle>{alert.title}</AlertTitle>
                <AlertDescription className="mt-1">
                  {alert.message}
                </AlertDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-6 w-6 flex-shrink-0 hover:bg-transparent",
                  alert.type === "success"
                    ? "hover:text-green-900"
                    : "hover:text-red-900"
                )}
                onClick={hideAlert}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </Alert>
        </div>
      )}

      <div className="space-y-4 max-w-full">
        {/* Header Section - Single line with title + count on left, controls on right */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-gray-900">Colleges</h1>
            <Badge className="h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
              {localPagination?.total || 0}
            </Badge>
          </div>

          <div className="flex items-center gap-3 w-full md:w-1/2">
            {/* Search Input */}
            <SearchInput
              id={id}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onSearchClear={handleSearchClear}
              searchInProgress={searchInProgress}
            />

            {/* Filter Button */}

            {/* Toggle columns visibility / Delete selected - Combined in single container */}
            <div className="flex items-center border rounded-md bg-white">
              {/* Column Toggle Icon */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleOpenFilterDrawer}
                className="relative rounded-r-none border-r"
              >
                <ListFilter className="opacity-60 h-4 w-4" />
                {getActiveFilterCount() > 0 && (
                  <Badge className="absolute -top-2 -right-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums text-xs">
                    {getActiveFilterCount()}
                  </Badge>
                )}
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      table.getSelectedRowModel().rows.length > 0 &&
                        "rounded-r-none border-r",
                      "rounded-l-none"
                    )}
                  >
                    <Columns3
                      className="opacity-60 h-4 w-4"
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="center"
                  className="w-56 p-3"
                >
                  <div className="space-y-3">
                    <div className="text-xs font-medium text-muted-foreground">
                      Toggle columns
                    </div>
                    <div className="space-y-4">
                      {table
                        .getAllColumns()
                        .filter((column) => column.getCanHide())
                        .map((column) => (
                          <div
                            key={column.id}
                            className="flex items-center gap-2"
                          >
                            <Checkbox
                              id={`${id}-column-${column.id}`}
                              checked={column.getIsVisible()}
                              onCheckedChange={(checked: boolean) =>
                                column.toggleVisibility(!!checked)
                              }
                            />
                            <Label
                              htmlFor={`${id}-column-${column.id}`}
                              className="flex grow justify-between gap-2 font-normal capitalize"
                            >
                              {column.id === "address"
                                ? "Address"
                                : column.id === "phone"
                                ? "Contact Details"
                                : column.id === "establishedYear"
                                ? "Established Year"
                                : column.id === "isActive"
                                ? "Status"
                                : column.id}
                            </Label>
                          </div>
                        ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Delete Icon - Only show when rows are selected */}
              {table.getSelectedRowModel().rows.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative rounded-l-none text-red-500 hover:text-red-500"
                    >
                      <Trash2 className="stroke-2 h-4 w-4" aria-hidden="true" />
                      <Badge className="absolute -top-2 -right-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums text-xs bg-red-500 hover:bg-red-500">
                        {table.getSelectedRowModel().rows.length}
                      </Badge>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Are you absolutely sure?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently
                        delete {table.getSelectedRowModel().rows.length}{" "}
                        selected{" "}
                        {table.getSelectedRowModel().rows.length === 1
                          ? "college"
                          : "colleges"}
                        .
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDeleteRows}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>

            {/* Add College Button - Icon only */}
            <Dialog
              open={isCreateDialogOpen}
              onOpenChange={setIsCreateDialogOpen}
            >
              <DialogTrigger asChild>
                <Button size="icon">
                  <Plus className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create New College</DialogTitle>
                  <DialogDescription>
                    Add a new educational institution to the system.
                  </DialogDescription>
                </DialogHeader>

                <Form {...createForm}>
                  <form
                    onSubmit={createForm.handleSubmit(handleCreateCollege)}
                    className="space-y-4"
                  >
                    <FormField
                      control={createForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>College Name *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g., Massachusetts Institute of Technology"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={createForm.control}
                      name="code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>College Code *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g., MIT"
                              maxLength={10}
                              {...field}
                              onChange={(e) =>
                                field.onChange(e.target.value.toUpperCase())
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={createForm.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="+91-8012345678"
                              {...field}
                              ref={withMask("+99-9999999999", {
                                placeholder: "",
                                showMaskOnHover: false,
                              })}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={createForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address *</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="info@college.edu"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={createForm.control}
                      name="establishedYear"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Established Year</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="2000"
                              min="1800"
                              max="2100"
                              {...field}
                              onChange={(e) =>
                                field.onChange(
                                  parseInt(e.target.value) ||
                                    new Date().getFullYear()
                                )
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={createForm.control}
                      name="isActive"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select
                            value={field.value ? "active" : "inactive"}
                            onValueChange={(value) =>
                              field.onChange(value === "active")
                            }
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="inactive">Inactive</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={createForm.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Address *</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Enter complete address"
                              rows={3}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsCreateDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Creating..." : "Create College"}
                      </Button>
                    </DialogFooter>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-md border bg-white">
          <Table className="table-fixed">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        style={{ width: `${header.getSize()}px` }}
                      >
                        {header.isPlaceholder ? null : header.column.getCanSort() ? (
                          <div
                            className={cn(
                              header.column.getCanSort() &&
                                "flex h-full cursor-pointer select-none items-center justify-between gap-2"
                            )}
                            onClick={header.column.getToggleSortingHandler()}
                            onKeyDown={(e) => {
                              if (
                                header.column.getCanSort() &&
                                (e.key === "Enter" || e.key === " ")
                              ) {
                                e.preventDefault();
                                header.column.getToggleSortingHandler()?.(e);
                              }
                            }}
                            tabIndex={
                              header.column.getCanSort() ? 0 : undefined
                            }
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: (
                                <ChevronUp
                                  className="shrink-0 opacity-60"
                                  size={16}
                                  strokeWidth={2}
                                  aria-hidden="true"
                                />
                              ),
                              desc: (
                                <ChevronDown
                                  className="shrink-0 opacity-60"
                                  size={16}
                                  strokeWidth={2}
                                  aria-hidden="true"
                                />
                              ),
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        ) : (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="last:py-0 px-4 py-2">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {isLoading ? (
                      <Loader variant="loading-dots" text="Loading" />
                    ) : searchTerm ? (
                      "No college found matching your search."
                    ) : (
                      "No college available."
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between gap-8">
          {/* Results per page */}
          <div className="flex items-center gap-3">
            <Label htmlFor={id} className="max-sm:sr-only">
              Rows per page
            </Label>
            <Select
              value={table.getState().pagination.pageSize.toString()}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger id={id} className="w-fit whitespace-nowrap">
                <SelectValue placeholder="Select number of results" />
              </SelectTrigger>
              <SelectContent className="[&_*[role=option]>span]:end-2 [&_*[role=option]>span]:start-auto [&_*[role=option]]:pe-8 [&_*[role=option]]:ps-2">
                {[5, 10, 25, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={pageSize.toString()}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Page number information */}
          <div className="flex grow justify-end whitespace-nowrap text-sm text-muted-foreground">
            <p
              className="whitespace-nowrap text-sm text-muted-foreground"
              aria-live="polite"
            >
              <span className="text-foreground">
                {table.getState().pagination.pageIndex *
                  table.getState().pagination.pageSize +
                  1}
                -
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) *
                    table.getState().pagination.pageSize,
                  localPagination?.total || 0
                )}
              </span>{" "}
              of{" "}
              <span className="text-foreground">
                {localPagination?.total || 0}
              </span>
            </p>
          </div>

          {/* Pagination buttons */}
          <div className="flex items-center gap-2">
            {/* First page button */}
            <Button
              size="icon"
              variant="outline"
              className="disabled:pointer-events-none disabled:opacity-50"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Go to first page"
            >
              <ChevronFirst className="stroke-2 h-4 w-4" aria-hidden="true" />
            </Button>
            {/* Previous page button */}
            <Button
              size="icon"
              variant="outline"
              className="disabled:pointer-events-none disabled:opacity-50"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Go to previous page"
            >
              <ChevronLeft className="stroke-2 h-4 w-4" aria-hidden="true" />
            </Button>
            {/* Next page button */}
            <Button
              size="icon"
              variant="outline"
              className="disabled:pointer-events-none disabled:opacity-50"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Go to next page"
            >
              <ChevronRight className="stroke-2 h-4 w-4" aria-hidden="true" />
            </Button>
            {/* Last page button */}
            <Button
              size="icon"
              variant="outline"
              className="disabled:pointer-events-none disabled:opacity-50"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Go to last page"
            >
              <ChevronLast className="stroke-2 h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        {/* Edit College Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit College</DialogTitle>
              <DialogDescription>
                Update the information for {editingCollege?.name}.
              </DialogDescription>
            </DialogHeader>

            <Form {...editForm}>
              <form
                onSubmit={editForm.handleSubmit(handleUpdateCollege)}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={editForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>College Name *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., Massachusetts Institute of Technology"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>College Code *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., MIT"
                            maxLength={10}
                            {...field}
                            onChange={(e) =>
                              field.onChange(e.target.value.toUpperCase())
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="+91-80-12345678"
                            {...field}
                            ref={withMask("+99-99-99999999", {
                              placeholder: "",
                              showMaskOnHover: false,
                            })}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address *</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="info@college.edu"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="establishedYear"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Established Year</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="2000"
                            min="1800"
                            max="2100"
                            {...field}
                            onChange={(e) =>
                              field.onChange(
                                parseInt(e.target.value) ||
                                  new Date().getFullYear()
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={editForm.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select
                          value={field.value ? "active" : "inactive"}
                          onValueChange={(value) =>
                            field.onChange(value === "active")
                          }
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="col-span-2">
                    <FormField
                      control={editForm.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Address *</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Enter complete address"
                              rows={3}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsEditDialogOpen(false);
                      setEditingCollege(null);
                      editForm.reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Updating..." : "Update College"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

function RowActions({
  row,
  onEdit,
  onRefresh,
  onAlert,
}: {
  row: Row<College>;
  onEdit: (college: College) => void;
  onRefresh: () => void;
  onAlert: (type: "success" | "error", title: string, message: string) => void;
}) {
  const college = row.original;

  const handleDelete = async () => {
    try {
      // Use the unified delete endpoint that handles both single and bulk operations
      await api.delete(`/colleges/${college.id}`);

      // Show success alert
      onAlert(
        "success",
        "College Deleted Successfully",
        `${college.name} (${college.code}) has been deleted successfully.`
      );

      // Refresh data using the callback
      onRefresh();
    } catch (error: any) {
      console.error("Failed to delete college:", error);

      // Show error alert
      onAlert(
        "error",
        "Delete Failed",
        error.response?.data?.message ||
          "Failed to delete college. Please try again."
      );
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex justify-end">
          <Button
            size="icon"
            variant="ghost"
            className="shadow-none"
            aria-label="Edit item"
          >
            <Ellipsis className="stroke-2 h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => onEdit(college)}>
            <span>Edit</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={handleDelete}
        >
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default Colleges;
